import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CodeIcon from "@mui/icons-material/Code";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import BlazeAILogo from "./BlazeAILogo";
import BlazeAIBrand from "./BlazeAIBrand";
import MarketingInfoBar from "./MarketingInfoBar";
import PageMeta from "./PageMeta";

// Shared keyframes for decorative animations
const KEYFRAMES = {
  "@keyframes ibFloatPulse": {
    "0%, 100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 0.6 },
    "50%": { transform: "translate(-50%, -52%) scale(1.06)", opacity: 0.85 },
  },
  "@keyframes ibDriftOrb": {
    "0%, 100%": { transform: "translate(0, 0)" },
    "50%": { transform: "translate(18px, -24px)" },
  },
  "@keyframes ibScrollBounce": {
    "0%, 100%": { transform: "translateY(0)", opacity: 0.6 },
    "50%": { transform: "translateY(6px)", opacity: 1 },
  },
  "@keyframes ibCursorBlink": {
    "0%, 49%": { opacity: 1 },
    "50%, 100%": { opacity: 0 },
  },
};

const EXAMPLE_PROMPTS = [
  {
    prompt: "Add a public S3 bucket with versioning and a 90-day lifecycle.",
    response:
      "Adding aws_s3_bucket.public_assets to ib-aws-infra-east-1 with versioning and 90-day Glacier transition. Your require-owner-tag policy flags a violation — want me to add owner = \"platform-team\"?",
  },
  {
    prompt: "This IaC plan keeps failing. Fix it.",
    response:
      "lambda_processor references aws_iam_role.lambda_exec.arn but it's missing from state. Creating it with minimum Lambda trust policy, patching line 47 in resources.tf. Re-running plan now.",
  },
  {
    prompt: "What's wasting money in my staging account?",
    response:
      "Scanned staging-us-west-2. 3 idle RDS instances at $312/mo, a forgotten NAT gateway at $33/mo, 4 unattached EBS volumes at $47/mo. Total: $392/mo savings. Open a cleanup PR?",
  },
];

const SUBAGENTS = [
  {
    icon: InventoryOutlinedIcon,
    name: "Inventory Agent",
    desc: "Knows every asset across your clouds, accounts, and SaaS tools. Answers questions grounded in the live graph, not stale exports.",
    example: "List prod S3 buckets without an owner tag.",
    tone: "teal",
  },
  {
    icon: PaidOutlinedIcon,
    name: "Cost Agent",
    desc: "Spots idle spend, forecasts the cost impact of every PR, and recommends commitments before you overprovision.",
    example: "What's wasting money in staging?",
    tone: "amber",
  },
  {
    icon: LightbulbOutlinedIcon,
    name: "Insights Agent",
    desc: "Turns intent into a query, a diff, or a ready-to-merge PR — grounded in your live inventory.",
    example: "Show me everything touching prod-data.",
    tone: "violet",
  },
  {
    icon: SellOutlinedIcon,
    name: "Naming Agent",
    desc: "Catches convention violations before they merge. Learns your org's tagging, naming, and placement rules.",
    example: "Who's breaking the prod-* naming rule?",
    tone: "teal",
  },
  {
    icon: CompareArrowsIcon,
    name: "Drift Agent",
    desc: "Continuously compares IaC against live state. Opens a remediation PR the moment drift appears.",
    example: "What's drifted since Friday?",
    tone: "amber",
  },
  {
    icon: CodeIcon,
    name: "IaC Engine",
    desc: "Generates and fixes IaC — Terraform today, OpenTofu and Pulumi on the roadmap. Follows your module structure and conventions.",
    example: "Codify these 4 untagged buckets into the data-lake stack.",
    tone: "green",
  },
];

const LLM_PROVIDERS = [
  { key: "anthropic", logoSrc: "/logos/anthropic.svg", name: "Claude 4.6 Opus" },
  { key: "openai", logoSrc: "/logos/openai.svg", name: "GPT 5.4" },
  { key: "bedrock", logoSrc: "/logos/awsbedrock.svg", name: "AWS Bedrock" },
];

export default function BlazeAIPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [mounted, setMounted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Scroll-triggered reveal state
  const promptsRef = useRef(null);
  const [promptsVisible, setPromptsVisible] = useState(false);
  const workflowRef = useRef(null);
  const [workflowVisible, setWorkflowVisible] = useState(false);
  const llmRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [llmVisible, setLlmVisible] = useState(false);
  const ctaRef = useRef(null);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onScroll = () => {
      if (window.scrollY > 80) setHasScrolled(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setPromptsVisible(true);
      setWorkflowVisible(true);
      setLlmVisible(true);
      setCtaVisible(true);
      return undefined;
    }
    if (typeof window === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target;
          if (target === promptsRef.current) setPromptsVisible(true);
          if (target === workflowRef.current) setWorkflowVisible(true);
          if (target === llmRef.current) setLlmVisible(true);
          if (target === ctaRef.current) setCtaVisible(true);
          observer.unobserve(target);
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );

    if (promptsRef.current) observer.observe(promptsRef.current);
    if (workflowRef.current) observer.observe(workflowRef.current);
    if (llmRef.current) observer.observe(llmRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, [reducedMotion]);

  const fadeUp = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  });

  const revealUp = (visible, delay = 0) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(32px)",
    transition: `opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  });

  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        position: "relative",
        ...KEYFRAMES,
      }}
    >
      <PageMeta
        title="BlazeAI — AI Subagents for the InfraBlaze Control Plane"
        description="Specialized AI subagents woven across InfraBlaze's control plane — cost, naming, inventory, policy, insights. Grounded in your live cloud, not pre-trained on it. BYO LLM supported."
        keywords="BlazeAI, AI subagents, cloud AI, agent driven infrastructure, cost AI, policy AI, InfraBlaze"
      />
      {/* ────────── Hero + scroll hint — fills the viewport so the "See it
           in action" arrow actually hides what's below the fold. ────────── */}
      <Box
        sx={{
          minHeight: { xs: "auto", md: "calc(100dvh - 56px)" },
          display: "flex",
          flexDirection: "column",
        }}
      >
      <Box
        sx={{
          position: "relative",
          flex: 1,
          pt: {
            xs: "calc(56px + env(safe-area-inset-top) + 40px)",
            md: "calc(56px + env(safe-area-inset-top) + 56px)",
          },
          pb: { xs: 3, md: 4 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {/* Floating glow orbs */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "40%",
            left: "50%",
            width: { xs: 500, md: 900 },
            height: { xs: 500, md: 900 },
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.24 : 0.13)}, transparent 60%)`,
            filter: "blur(90px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibFloatPulse 12s ease-in-out infinite",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "10%",
            right: "-5%",
            width: { xs: 300, md: 500 },
            height: { xs: 300, md: 500 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, isDark ? 0.18 : 0.10)}, transparent 65%)`,
            filter: "blur(70px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibDriftOrb 15s ease-in-out infinite",
          }}
        />

        <Container
          maxWidth="md"
          sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}
        >
          <Stack spacing={3} alignItems="center" textAlign="center">
            {/* BlazeAI animated avatar */}
            <Box
              sx={{
                ...fadeUp(0),
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: 140, md: 180 },
                height: { xs: 140, md: 180 },
              }}
            >
              <BlazeAILogo
                size={180}
                isWorking={!reducedMotion}
                flameBoost={1.5}
              />
            </Box>

            <Typography
              variant="h1"
              sx={{
                ...fadeUp(150),
                fontWeight: 950,
                letterSpacing: -1.0,
                lineHeight: 1.04,
                maxWidth: 800,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.05rem" },
              }}
            >
              Meet <BlazeAIBrand />
            </Typography>

            <Typography
              variant="h6"
              color="text.primary"
              sx={{
                ...fadeUp(280),
                maxWidth: 700,
                lineHeight: 1.7,
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.4rem" },
              }}
            >
              Your AI infrastructure engineer. Built into every workflow.
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                ...fadeUp(400),
                fontSize: { xs: "1rem", md: "1.15rem" },
                lineHeight: 1.85,
                maxWidth: 760,
              }}
            >
              <BlazeAIBrand /> isn't a chatbot bolted onto a dashboard. It's an
              AI agent woven into the fabric of InfraBlaze — with full knowledge
              of your cloud resources, costs, policies, and architecture.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* ────────── LLM providers — compact strip ────────── */}
      <Box sx={{ pb: { xs: 3, md: 3 }, ...fadeUp(520) }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              fontWeight: 700,
              letterSpacing: 1.2,
              color: "text.secondary",
              fontSize: "0.7rem",
              mb: 2.5,
            }}
          >
            POWERED BY
          </Typography>

          {/* Provider strip */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={0}
            justifyContent="center"
            alignItems="center"
            divider={
              <Box
                sx={{
                  display: { xs: "none", sm: "block" },
                  width: "1px",
                  height: 36,
                  background: `linear-gradient(180deg, transparent, ${alpha(theme.palette.divider, 0.8)}, transparent)`,
                  mx: 0.5,
                }}
              />
            }
          >
            {LLM_PROVIDERS.map((p, idx) => (
              <Stack
                key={p.key}
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.25, sm: 0 },
                  ...fadeUp(600 + idx * 80),
                  transition: "transform 0.25s ease",
                  "&:hover": { transform: "translateY(-2px)" },
                }}
              >
                <Box
                  component="img"
                  src={p.logoSrc}
                  alt={p.name}
                  sx={{
                    height: 22,
                    width: "auto",
                    filter: isDark
                      ? "brightness(0) invert(1)"
                      : "brightness(0)",
                    opacity: 0.85,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.92rem",
                    letterSpacing: -0.1,
                    color: "text.primary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.name}
                </Typography>
              </Stack>
            ))}

            {/* BYO LLM */}
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                width: "1px",
                height: 36,
                background: `linear-gradient(180deg, transparent, ${alpha(theme.palette.divider, 0.8)}, transparent)`,
                mx: 0.5,
              }}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 1.25, sm: 0 },
                ...fadeUp(840),
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                }}
              >
                <Typography sx={{ fontSize: 13, lineHeight: 1 }}>🔒</Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.92rem",
                  letterSpacing: -0.1,
                  color: "primary.main",
                  whiteSpace: "nowrap",
                }}
              >
                Bring your own
              </Typography>
            </Stack>
          </Stack>

          {/* Privacy tagline */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 2.5,
              fontSize: "0.82rem",
              lineHeight: 1.6,
              opacity: 0.8,
            }}
          >
            Switch models per task or org-wide. Bring your own LLM — keep
            your company's data within your control.
          </Typography>
        </Container>
      </Box>

      {/* Scroll hint — fades out permanently once user scrolls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pb: { xs: 3, md: 4 },
          ...fadeUp(900),
          opacity: hasScrolled ? 0 : (mounted ? 1 : 0),
          pointerEvents: hasScrolled ? "none" : "auto",
          transition: "opacity 0.5s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) 900ms",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            animation: reducedMotion ? "none" : "ibScrollBounce 2s ease-in-out infinite",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: 1,
              opacity: 0.7,
            }}
          >
            SEE IT IN ACTION
          </Typography>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRight: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
              transform: "rotate(45deg)",
            }}
          />
        </Box>
      </Box>
      </Box>

      {/* ────────── Chat demo + video loop ────────── */}
      <Box
        ref={promptsRef}
        sx={{
          pb: { xs: 7, md: 11 },
          ...revealUp(promptsVisible),
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 4, md: 5 },
              alignItems: { md: "stretch" },
            }}
          >
            {/* LEFT: chat cards — flex:1, fills remaining space */}
            <Box sx={{ flex: 1, display: "flex", minWidth: 0 }}>
              <Stack spacing={2.25} sx={{ width: "100%", height: "100%" }}>
                {EXAMPLE_PROMPTS.map((ex, idx) => (
                  <Box
                    key={ex.prompt}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      borderRadius: "16px",
                      border: "1px solid",
                      borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22),
                      backgroundColor: isDark
                        ? alpha("#0a1420", 0.88)
                        : alpha("#f3f7fb", 0.96),
                      backdropFilter: "blur(12px)",
                      p: { xs: 2.5, md: 3 },
                      transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                      boxShadow: isDark
                        ? `0 10px 30px ${alpha("#000", 0.32)}`
                        : `0 10px 30px ${alpha(theme.palette.primary.main, 0.08)}`,
                      opacity: promptsVisible ? 1 : 0,
                      transform: promptsVisible ? "translateY(0)" : "translateY(20px)",
                      transitionProperty: "opacity, transform, border-color, box-shadow",
                      transitionDuration: "0.7s",
                      transitionDelay: `${idx * 140}ms`,
                      transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                      "&:hover": {
                        borderColor: alpha(theme.palette.primary.main, 0.6),
                        boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}`,
                      },
                    }}
                  >
                    {/* User prompt row */}
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "0 0 auto",
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.14),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                        }}
                      >
                        <PersonOutlineIcon sx={{ color: "primary.main", fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: 1,
                            color: "primary.main",
                            display: "block",
                            mb: 0.5,
                            fontSize: "0.72rem",
                          }}
                        >
                          YOU
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: "text.primary",
                            fontSize: { xs: "0.95rem", md: "1rem" },
                            lineHeight: 1.55,
                          }}
                        >
                          {ex.prompt}
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              width: "7px",
                              height: "1em",
                              ml: 0.5,
                              verticalAlign: "text-bottom",
                              backgroundColor: theme.palette.primary.main,
                              animation: reducedMotion
                                ? "none"
                                : "ibCursorBlink 1s steps(2) infinite",
                            }}
                          />
                        </Typography>
                      </Box>
                    </Stack>

                    {/* BlazeAI response row */}
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{
                        mt: 2.5,
                        pt: 2.5,
                        borderTop: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "0 0 auto",
                          width: 36,
                          height: 36,
                        }}
                      >
                        <BlazeAILogo size={34} isWorking={false} flameBoost={1} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: 1,
                            color: "primary.main",
                            display: "block",
                            mb: 0.5,
                            fontSize: "0.72rem",
                          }}
                        >
                          BLAZEAI
                        </Typography>
                        <Typography
                          sx={{ lineHeight: 1.65, fontSize: { xs: "0.9rem", md: "0.95rem" }, color: "text.primary" }}
                        >
                          {ex.response}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* RIGHT: BlazeAI panel — fixed width, self-sizing, decoupled */}
            <Box
              sx={{
                flex: { md: "0 0 46%" },
                width: { xs: "100%" },
                alignSelf: { md: "center" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "862 / 1296",
                  borderRadius: "14px",
                  overflow: "hidden",
                  // outline renders OUTSIDE the element — never overlays the video
                  outline: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.45 : 0.32)}`,
                  outlineOffset: -1,
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.15)}, 0 0 40px ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.18)}, 0 0 100px ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12)}, 0 24px 70px ${alpha("#000", isDark ? 0.5 : 0.25)}`,
                }}
              >
                <Box
                  component="video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  sx={{
                    position: "absolute",
                    top: -1,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100%",
                    height: "calc(100% + 1px)",
                    objectFit: "cover",
                    display: "block",
                  }}
                >
                  <source src="/videos/blazeAi-final-loop-1.mp4" type="video/mp4" />
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ────────── Subagents grid ────────── */}
      <Box
        ref={workflowRef}
        sx={{
          pb: { xs: 7, md: 10 },
          ...revealUp(workflowVisible),
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ mb: { xs: 5, md: 7 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                letterSpacing: -0.5,
                fontSize: { xs: "1.9rem", md: "2.6rem" },
              }}
            >
              Specialized subagents.
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ maxWidth: 640, lineHeight: 1.7, fontSize: "1.02rem" }}
            >
              <BlazeAIBrand /> isn't a single model with a system prompt. Each
              subagent runs grounded in your live cloud — answering the
              questions that domain actually gets asked.
            </Typography>
          </Stack>

          <Grid container spacing={{ xs: 2.5, md: 3 }}>
            {SUBAGENTS.map((agent, idx) => {
              const Icon = agent.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={agent.name}>
                  <Box
                    sx={{
                      height: "100%",
                      p: { xs: 2.75, md: 3 },
                      borderRadius: "16px",
                      background: "var(--ink-1)",
                      border: "1px solid var(--ink-3)",
                      boxShadow: "var(--glow-card)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      opacity: workflowVisible ? 1 : 0,
                      transform: workflowVisible
                        ? "translateY(0)"
                        : "translateY(20px)",
                      transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${
                        idx * 70
                      }ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${
                        idx * 70
                      }ms, border-color 0.2s ease`,
                      "&:hover": {
                        borderColor: "var(--teal)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `color-mix(in oklab, var(--${agent.tone}) 14%, transparent)`,
                        color: `var(--${agent.tone})`,
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: -0.2,
                        fontSize: "1.05rem",
                        color: "var(--ink-9)",
                      }}
                    >
                      {agent.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: "var(--ink-7)",
                        lineHeight: 1.6,
                        fontSize: "0.93rem",
                      }}
                    >
                      {agent.desc}
                    </Typography>
                    <Box
                      sx={{
                        mt: "auto",
                        pt: 1.25,
                        borderTop: "1px dashed var(--ink-3)",
                        fontFamily: "var(--f-mono)",
                        fontSize: "0.78rem",
                        lineHeight: 1.55,
                        color: "var(--ink-6)",
                      }}
                    >
                      <Box component="span" sx={{ color: "var(--teal)", mr: 0.75 }}>
                        ›
                      </Box>
                      {agent.example}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ────────── Final CTA ────────── */}
      <Box
        ref={ctaRef}
        sx={{
          pb: { xs: 8, md: 12 },
          ...revealUp(ctaVisible),
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              position: "relative",
              borderRadius: "22px",
              overflow: "hidden",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, isDark ? 0.5 : 0.35),
              backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.96),
              backdropFilter: "blur(16px)",
              p: { xs: 4, md: 6 },
              textAlign: "center",
              boxShadow: `0 30px 100px ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.15)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: `radial-gradient(500px 200px at 50% 0%, ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.16)}, transparent 60%), radial-gradient(500px 250px at 50% 100%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, isDark ? 0.2 : 0.1)}, transparent 60%)`,
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              {/* Avatar on card */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 80,
                  height: 80,
                  mb: 2,
                }}
              >
                <BlazeAILogo size={80} isWorking={!reducedMotion} flameBoost={1.3} />
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  letterSpacing: -0.5,
                  lineHeight: 1.15,
                  mb: 1.5,
                  fontSize: { xs: "1.8rem", md: "2.3rem" },
                }}
              >
                Ready to meet your AI infrastructure engineer?
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  maxWidth: 560,
                  mx: "auto",
                  mb: 4,
                  lineHeight: 1.75,
                  fontSize: "1.05rem",
                }}
              >
                See <BlazeAIBrand /> live — auto-generating IaC, remediating
                drift, fixing failed plans, and surfacing cost wins inside the
                Workbench.
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ rowGap: 1.5 }}
              >
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  startIcon={<PlayCircleOutlineIcon />}
                  sx={{
                    fontWeight: 900,
                    borderRadius: 999,
                    px: 3.5,
                    textTransform: "none",
                    color: "common.white !important",
                    boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    "&:hover": {
                      color: "common.white !important",
                      transform: "translateY(-2px)",
                      boxShadow: `0 14px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                  }}
                >
                  See BlazeAI in action
                </Button>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="outlined"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    fontWeight: 900,
                    borderRadius: 999,
                    px: 3.5,
                    textTransform: "none",
                    borderWidth: 1.5,
                    color: isDark ? "#fff" : "text.primary",
                    borderColor: alpha(isDark ? "#fff" : theme.palette.text.primary, 0.3),
                    transition: "transform 0.25s ease, border-color 0.25s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderWidth: 1.5,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  Request access
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <MarketingInfoBar />
      </Box>
    </Box>
  );
}
